import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import toast from 'react-hot-toast';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Switch,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  useMediaQuery,
  useTheme as useMuiTheme
} from '@mui/material';
import {
  UserCircleIcon,
  BellIcon,
  ClockIcon,
  ChartBarIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import AppIconButton from '../components/ui/AppIconButton';

interface StewardProfile {
  id: number;
  username: string;
  email: string;
  stewardCode?: string;
  isOnDuty: boolean;
  dutyStartedAt?: string;
  assignedTables: number;
  completedRequests: number;
  averageResponseTime: number;
  todayStats: {
    ordersPlaced: number;
    requestsHandled: number;
    tablesServed: number;
  };
}

const StewardAccount: React.FC = () => {
  const { user, signout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [profile, setProfile] = useState<StewardProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [changingDutyStatus, setChangingDutyStatus] = useState(false);
  const [showDutyDialog, setShowDutyDialog] = useState(false);
  const [dutyAction, setDutyAction] = useState<'start' | 'end'>('start');
  const [dutyNotes, setDutyNotes] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/steward/profile');
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleDutyStatusChange = (isOnDuty: boolean) => {
    setDutyAction(isOnDuty ? 'start' : 'end');
    setShowDutyDialog(true);
  };

  const confirmDutyChange = async () => {
    try {
      setChangingDutyStatus(true);
      await api.post('/api/steward/duty-status', {
        isOnDuty: dutyAction === 'start',
        notes: dutyNotes
      });
      
      toast.success(dutyAction === 'start' ? 'You are now on duty' : 'You are now off duty');
      fetchProfile();
      setShowDutyDialog(false);
      setDutyNotes('');
    } catch (error) {
      console.error('Failed to change duty status:', error);
      toast.error('Failed to update duty status');
    } finally {
      setChangingDutyStatus(false);
    }
  };

  const handleLogout = () => {
    // Use centralized signout so users see unified "Logging out..." toast and proper redirects
    signout();
  };

  if (loading || !profile) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'var(--bg-sunken)', pb: 12 }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderBottom: '1px solid var(--border-subtle)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}
      >
        <Box sx={{ 
          p: { xs: 2, sm: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Box display="flex" alignItems="center" gap={2} width={{ xs: '100%', sm: 'auto' }}>
            <AppIconButton
              icon={<ArrowLeftIcon style={{ width: 20, height: 20 }} />}
              tooltip="Back to Dashboard"
              variant="secondary"
              onClick={() => navigate('/steward/dashboard')}
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                color: 'var(--text-primary)'
              }}
            >
              Account
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Profile Section */}
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Card sx={{ mb: 3, overflow: 'visible' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box 
              display="flex" 
              alignItems={isMobile ? 'flex-start' : 'center'} 
              flexDirection={isMobile ? 'column' : 'row'}
              gap={isMobile ? 2 : 3} 
              mb={3}
            >
              <Box 
                display="flex" 
                alignItems="center" 
                gap={2}
                width={isMobile ? '100%' : 'auto'}
              >
                <Avatar
                  sx={{
                    width: { xs: 60, sm: 80 },
                    height: { xs: 60, sm: 80 },
                    bgcolor: theme.primary,
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    fontWeight: 700
                  }}
                >
                  {profile.username.charAt(0).toUpperCase()}
                </Avatar>
                <Box flex={1}>
                  <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700} color="text.primary">
                    {profile.username}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {profile.email}
                  </Typography>
                  {profile.stewardCode && (
                    <Chip 
                      label={`Code: ${profile.stewardCode}`} 
                      size="small" 
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              </Box>
              <Box alignSelf={isMobile ? 'flex-start' : 'center'}>
                <Chip
                  icon={profile.isOnDuty ? <CheckCircleIcon style={{ width: 16, height: 16 }} /> : <XCircleIcon style={{ width: 16, height: 16 }} />}
                  label={profile.isOnDuty ? 'On Duty' : 'Off Duty'}
                  color={profile.isOnDuty ? 'success' : 'default'}
                  variant={profile.isOnDuty ? 'filled' : 'outlined'}
                />
              </Box>
            </Box>

            {/* Duty Toggle */}
            <Paper 
              sx={{ 
                p: 2, 
                backgroundColor: profile.isOnDuty ? '#f0fdf4' : '#f8fafc',
                border: `1px solid ${profile.isOnDuty ? '#86efac' : '#e2e8f0'}`
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    Duty Status
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {profile.isOnDuty && profile.dutyStartedAt
                      ? `On duty since ${format(new Date(profile.dutyStartedAt), 'h:mm a')}`
                      : 'Toggle to start your shift'}
                  </Typography>
                </Box>
                <Switch
                  checked={profile.isOnDuty}
                  onChange={(e) => handleDutyStatusChange(e.target.checked)}
                  color="success"
                />
              </Box>
            </Paper>

            {/* Today's Stats */}
            <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mt: 2 }}>
              <Grid item xs={4}>
                <Box textAlign="center" sx={{ p: { xs: 1, sm: 0 } }}>
                  <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700} color="primary">
                    {profile.todayStats.ordersPlaced}
                  </Typography>
                  <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
                    {isMobile ? 'Orders' : 'Orders Placed'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center" sx={{ p: { xs: 1, sm: 0 } }}>
                  <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700} color="success.main">
                    {profile.todayStats.requestsHandled}
                  </Typography>
                  <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
                    {isMobile ? 'Requests' : 'Requests Handled'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center" sx={{ p: { xs: 1, sm: 0 } }}>
                  <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700} color="info.main">
                    {profile.todayStats.tablesServed}
                  </Typography>
                  <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
                    {isMobile ? 'Tables' : 'Tables Served'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Settings Menu */}
        <Card>
          <List sx={{ p: 0 }}>
            <ListItem 
              button 
              onClick={() => navigate('/steward/tables')}
              sx={{ 
                py: { xs: 2, sm: 1.5 },
                minHeight: { xs: 64, sm: 'auto' }
              }}
            >
              <ListItemIcon>
                <TableCellsIcon style={{ width: 24, height: 24, color: theme.primary }} />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography variant={isMobile ? 'body1' : 'body1'} fontWeight={500}>
                    My Tables
                  </Typography>
                }
                secondary={
                  <Typography variant={isMobile ? 'body2' : 'body2'} color="text.secondary">
                    {`${profile.assignedTables} tables assigned`}
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <AppIconButton
                  icon={<ArrowRightOnRectangleIcon style={{ width: 20, height: 20 }} />}
                  tooltip="Go to My Tables"
                  variant="ghost"
                  edge="end"
                  size={isMobile ? 'medium' : 'small'}
                />
              </ListItemSecondaryAction>
            </ListItem>

            <Divider />

            <ListItem
              button
              onClick={() => navigate('/steward/attendance')}
              sx={{
                py: { xs: 2, sm: 1.5 },
                minHeight: { xs: 64, sm: 'auto' }
              }}
            >
              <ListItemIcon>
                <ClockIcon style={{ width: 24, height: 24, color: theme.success }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant={isMobile ? 'body1' : 'body1'} fontWeight={500}>
                    Attendance
                  </Typography>
                }
                secondary={
                  <Typography variant={isMobile ? 'body2' : 'body2'} color="text.secondary">
                    Check in/out with face ID
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <AppIconButton
                  icon={<ArrowRightOnRectangleIcon style={{ width: 20, height: 20 }} />}
                  tooltip="Go to Attendance"
                  variant="ghost"
                  edge="end"
                  size={isMobile ? 'medium' : 'small'}
                />
              </ListItemSecondaryAction>
            </ListItem>

            <Divider />

            <ListItem
              button
              onClick={() => navigate('/steward/performance')}
              sx={{ 
                py: { xs: 2, sm: 1.5 },
                minHeight: { xs: 64, sm: 'auto' }
              }}
            >
              <ListItemIcon>
                <ChartBarIcon style={{ width: 24, height: 24, color: theme.warning }} />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography variant={isMobile ? 'body1' : 'body1'} fontWeight={500}>
                    Performance
                  </Typography>
                }
                secondary={
                  <Typography variant={isMobile ? 'body2' : 'body2'} color="text.secondary">
                    {`${profile.averageResponseTime}s avg response time`}
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <AppIconButton
                  icon={<ArrowRightOnRectangleIcon style={{ width: 20, height: 20 }} />}
                  tooltip="Go to Performance"
                  variant="ghost"
                  edge="end"
                  size={isMobile ? 'medium' : 'small'}
                />
              </ListItemSecondaryAction>
            </ListItem>

            <Divider />

            <ListItem
              button
              onClick={() => navigate('/steward/shift-history')}
              sx={{ 
                py: { xs: 2, sm: 1.5 },
                minHeight: { xs: 64, sm: 'auto' }
              }}
            >
              <ListItemIcon>
                <CalendarIcon style={{ width: 24, height: 24, color: theme.primary || '#5B9BD5' }} />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography variant={isMobile ? 'body1' : 'body1'} fontWeight={500}>
                    Shift History
                  </Typography>
                }
                secondary={
                  <Typography variant={isMobile ? 'body2' : 'body2'} color="text.secondary">
                    View past shifts and performance
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <AppIconButton
                  icon={<ArrowRightOnRectangleIcon style={{ width: 20, height: 20 }} />}
                  tooltip="Go to Shift History"
                  variant="ghost"
                  edge="end"
                  size={isMobile ? 'medium' : 'small'}
                />
              </ListItemSecondaryAction>
            </ListItem>

            <Divider />

            <ListItem
              button
              onClick={() => navigate('/steward/notifications')}
              sx={{ 
                py: { xs: 2, sm: 1.5 },
                minHeight: { xs: 64, sm: 'auto' }
              }}
            >
              <ListItemIcon>
                <BellIcon style={{ width: 24, height: 24, color: theme.secondary }} />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography variant={isMobile ? 'body1' : 'body1'} fontWeight={500}>
                    Notifications
                  </Typography>
                }
                secondary={
                  <Typography variant={isMobile ? 'body2' : 'body2'} color="text.secondary">
                    Manage notification preferences
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <AppIconButton
                  icon={<ArrowRightOnRectangleIcon style={{ width: 20, height: 20 }} />}
                  tooltip="Go to Notifications"
                  variant="ghost"
                  edge="end"
                  size={isMobile ? 'medium' : 'small'}
                />
              </ListItemSecondaryAction>
            </ListItem>

            <Divider />

            <ListItem
              button
              onClick={() => navigate('/steward/settings')}
              sx={{ 
                py: { xs: 2, sm: 1.5 },
                minHeight: { xs: 64, sm: 'auto' }
              }}
            >
              <ListItemIcon>
                <CogIcon style={{ width: 24, height: 24, color: theme.textSecondary }} />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography variant={isMobile ? 'body1' : 'body1'} fontWeight={500}>
                    Settings
                  </Typography>
                }
                secondary={
                  <Typography variant={isMobile ? 'body2' : 'body2'} color="text.secondary">
                    App preferences and configuration
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <AppIconButton
                  icon={<ArrowRightOnRectangleIcon style={{ width: 20, height: 20 }} />}
                  tooltip="Go to Settings"
                  variant="ghost"
                  edge="end"
                  size={isMobile ? 'medium' : 'small'}
                />
              </ListItemSecondaryAction>
            </ListItem>

            <Divider />

            <ListItem
              button
              onClick={handleLogout}
              sx={{ 
                py: { xs: 2, sm: 1.5 },
                minHeight: { xs: 64, sm: 'auto' }
              }}
            >
              <ListItemIcon>
                <ArrowRightOnRectangleIcon style={{ width: 24, height: 24, color: theme.danger || '#DC2626' }} />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography variant={isMobile ? 'body1' : 'body1'} fontWeight={500}>
                    Logout
                  </Typography>
                }
                secondary={
                  <Typography variant={isMobile ? 'body2' : 'body2'} color="text.secondary">
                    End your session
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </Card>
      </Box>

      {/* Duty Status Dialog */}
      <Dialog 
        open={showDutyDialog} 
        onClose={() => setShowDutyDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            m: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant={isMobile ? 'h6' : 'h5'}>
            {dutyAction === 'start' ? 'Start Duty' : 'End Duty'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 }, maxHeight: '80vh', overflowY: 'auto' }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {dutyAction === 'start' 
              ? 'You are about to start your duty shift. Make sure you are ready to handle customer requests.'
              : 'You are about to end your duty shift. Any pending requests will be reassigned.'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={isMobile ? 4 : 3}
            label="Notes (optional)"
            value={dutyNotes}
            onChange={(e) => setDutyNotes(e.target.value)}
            placeholder={dutyAction === 'start' 
              ? 'e.g., Starting morning shift...'
              : 'e.g., Shift completed, 2 pending requests...'}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 2, sm: 3 },
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0
        }}>
          <Button 
            onClick={() => setShowDutyDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'large' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={confirmDutyChange}
            disabled={changingDutyStatus}
            color={dutyAction === 'start' ? 'success' : 'error'}
            fullWidth={isMobile}
            size={isMobile ? 'large' : 'medium'}
            sx={{ minHeight: isMobile ? 48 : 'auto' }}
          >
            {changingDutyStatus ? 'Processing...' : dutyAction === 'start' ? 'Start Duty' : 'End Duty'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StewardAccount;